// Currency display helpers. Every amount belongs to one property's currency;
// the API sends it as a code ("EUR") and/or `currency_details`
// ({ code, name, symbol, decimal_places }). Amounts are never converted.

export const DEFAULT_CURRENCY = {
  code: "GBP",
  name: "British Pound",
  symbol: "£",
  decimal_places: 2,
};

// Accepts a currency_details object, a code string, or nothing (-> GBP).
export function resolveCurrency(currency) {
  if (!currency) return DEFAULT_CURRENCY;
  if (typeof currency === "string") {
    if (currency === DEFAULT_CURRENCY.code) return DEFAULT_CURRENCY;
    return { code: currency, name: currency, symbol: symbolFor(currency), decimal_places: 2 };
  }
  return {
    code: currency.code || DEFAULT_CURRENCY.code,
    name: currency.name || currency.code,
    symbol: currency.symbol || symbolFor(currency.code),
    decimal_places: Number.isInteger(currency.decimal_places) ? currency.decimal_places : 2,
  };
}

function symbolFor(code) {
  try {
    const part = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((p) => p.type === "currency");
    return part ? part.value : code;
  } catch {
    return code;
  }
}

// "£1,234.50", "€1,234.50", "$95.00". Formatting is driven by the currency,
// not the viewer's locale.
export function formatMoney(amount, currency) {
  const c = resolveCurrency(currency);
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: c.code,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: c.decimal_places,
      maximumFractionDigits: c.decimal_places,
    }).format(value);
  } catch {
    const sign = value < 0 ? "-" : "";
    return `${sign}${c.symbol}${Math.abs(value).toFixed(c.decimal_places)}`;
  }
}

// "GBP (£)" — used in headers so "$" can't be mistaken for another dollar.
export function currencyLabel(currency) {
  const c = resolveCurrency(currency);
  return c.symbol && c.symbol !== c.code ? `${c.code} (${c.symbol})` : c.code;
}

// jsPDF's built-in fonts only cover Windows-1252 (£, €, $, ¥ are fine).
// For anything else fall back to "PLN 1,234.50".
const WINANSI_SAFE = /^[\x20-\x7E£€¥¢§©®°µ¤]*$/;

export function pdfMoney(amount, currency) {
  const c = resolveCurrency(currency);
  const formatted = formatMoney(amount, c);
  if (WINANSI_SAFE.test(formatted)) return formatted;
  const value = Number(amount) || 0;
  return `${c.code} ${value.toLocaleString("en-GB", {
    minimumFractionDigits: c.decimal_places,
    maximumFractionDigits: c.decimal_places,
  })}`;
}

export function pdfCurrencyLabel(currency) {
  const c = resolveCurrency(currency);
  return WINANSI_SAFE.test(c.symbol) && c.symbol !== c.code ? `${c.code} (${c.symbol})` : c.code;
}

// Totals per currency, never summed across currencies:
// [{ currency: "GBP", total: 340 }, { currency: "EUR", total: 210 }]
export function totalsByCurrency(items, getAmount, getCurrency) {
  const totals = new Map();
  items.forEach((item) => {
    const code = resolveCurrency(getCurrency(item)).code;
    totals.set(code, (totals.get(code) || 0) + (Number(getAmount(item)) || 0));
  });
  return Array.from(totals, ([currency, total]) => ({ currency, total }));
}
